import React, { useRef, useEffect, useState } from 'react';
import { 
  Box, 
  Card,
  styled,
  Fab,
  TextField,
  Collapse,
  IconButton,
  Paper,
  InputAdornment,
  Button,
  Typography,
  Tooltip,
  Fade
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import SummarizeIcon from '@mui/icons-material/Summarize';
import LibraryBooksRoundedIcon from '@mui/icons-material/LibraryBooksRounded';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import FeedbackIcon from '@mui/icons-material/Feedback';

const CardSection = styled(Box)(({ theme }) => ({
  top: '64px',
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  position: 'relative',
}));

const CardContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: '400px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '660px',
  perspective: '1000px',
  padding: theme.spacing(2),
}));

const StyledCard = styled(Card)(({ theme, ishovered, isfortune, isselected }) => ({
  position: 'absolute',
  width: 220,
  height: 360,
  backgroundColor: 'rgba(255, 255, 255, 0.95)',
  cursor: 'pointer',
  transition: 'all 0.4s ease',
  transform: isfortune === 'true' 
    ? 'translateX(-50%)' 
    : (isselected === 'true' ? 'scale(1.05)' : 'scale(1) rotateY(15deg)'),
  opacity: isselected === 'true' ? 1 : 0.5,
  transformStyle: 'preserve-3d',
  borderRadius: '20px',
  padding: '12px',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  backdropFilter: 'blur(10px)',
  boxShadow: isselected === 'true' 
    ? '0 15px 30px rgba(0,0,0,0.15), 0 0 0 1px rgba(255,255,255,0.4) inset'
    : '0 8px 20px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.2) inset',
  '&:hover': {
    opacity: 1,
    transform: isfortune === 'true'
      ? 'translateX(-50%) scale(1.05)'
      : 'scale(1.05)',
  },
}));

const CardImage = styled('img')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  borderRadius: '15px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1) inset',
  border: '1px solid rgba(255, 255, 255, 0.2)',
});

const CardTitle = styled(Box)(({ theme, ishovered }) => ({
  position: 'absolute',
  bottom: -45,
  left: 0,
  right: 0,
  textAlign: 'center',
  fontFamily: "'Recoleta', serif",
  fontWeight: 600,
  fontSize: '1.1rem',
  color: 'rgba(0, 0, 0, 0.7)',
  opacity: ishovered === 'true' ? 1 : 0,
  transform: ishovered === 'true' ? 'translateY(0)' : 'translateY(10px)',
  transition: 'all 0.3s ease',
  textShadow: '0 2px 4px rgba(0,0,0,0.1)',
}));

const AnimatedBackground = styled(Box)(({ colors }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100vw',
  height: '100vh',
  backgroundImage: colors ? 
    colors.map((color, index) => 
      `radial-gradient(closest-side, ${color}, ${color}00)`
    ).join(',') :
    `
      radial-gradient(closest-side, #ece5d4, #ece5d400),
      radial-gradient(closest-side, #e6cda8, #e6cda800),
      radial-gradient(closest-side, #8dc1c9, #8dc1c900)
    `,
  backgroundSize: colors ?
    colors.map(() => '130vmax 130vmax').join(',') :
    '130vmax 130vmax, 130vmax 130vmax, 130vmax 130vmax',
  backgroundPosition: colors ?
    colors.map((_, index) => `${-30 + (index * 20)}vmax ${-30 + (index * 20)}vmax`).join(',') :
    '-60vmax -60vmax, 0vmax 0vmax, 60vmax 60vmax',
  backgroundRepeat: 'no-repeat',
  animation: 'movement 20s linear infinite',
  zIndex: -1,
  transition: 'all 0.5s ease-in-out',
  '&::after': {
    content: '""',
    position: 'absolute',
    width: '100%',
    height: '100%',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
  },
  '@keyframes movement': {
    '0%, 100%': {
      backgroundSize: '130vmax 130vmax, 80vmax 80vmax, 90vmax 90vmax, 110vmax 110vmax, 90vmax 90vmax',
      backgroundPosition: '-80vmax -80vmax, 60vmax -30vmax, 10vmax 10vmax, -30vmax -10vmax, 50vmax 50vmax',
    },
    '25%': {
      backgroundSize: '100vmax 100vmax, 90vmax 90vmax, 100vmax 100vmax, 90vmax 90vmax, 60vmax 60vmax',
      backgroundPosition: '-60vmax -90vmax, 50vmax -40vmax, 0vmax -20vmax, -40vmax -20vmax, 40vmax 60vmax',
    },
    '50%': {
      backgroundSize: '80vmax 80vmax, 110vmax 110vmax, 80vmax 80vmax, 60vmax 60vmax, 80vmax 80vmax',
      backgroundPosition: '-50vmax -70vmax, 40vmax -30vmax, 10vmax 0vmax, 20vmax 10vmax, 30vmax 70vmax',
    },
    '75%': {
      backgroundSize: '90vmax 90vmax, 90vmax 90vmax, 100vmax 100vmax, 90vmax 90vmax, 70vmax 70vmax',
      backgroundPosition: '-50vmax -40vmax, 50vmax -30vmax, 20vmax 0vmax, -10vmax 10vmax, 40vmax 60vmax',
    },
  },
}));

const NavigationButtons = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '15%',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  gap: theme.spacing(2),
  zIndex: 110,
}));

const NavButton = styled(Button)(({ theme, active }) => ({
  fontFamily: "'EB Garamond', serif",
  fontSize: '1.1rem',
  padding: theme.spacing(0.8, 3),
  borderRadius: theme.spacing(3),
  backgroundColor: active ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.85)',
  color: active ? '#fff' : theme.palette.primary.main,
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  backdropFilter: 'blur(5px)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : 'rgba(255, 255, 255, 0.95)',
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
  },
}));

const ExpandableFab = styled(Fab)(({ theme }) => ({
  boxShadow: '0 6px 20px rgba(0,0,0,0.2)',
  backgroundColor: theme.palette.primary.main,
  color: '#fff',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
    transform: 'translateY(-3px)',
    boxShadow: '0 8px 25px rgba(0,0,0,0.25)',
  },
  transition: 'all 0.3s ease',
}));

const FollowUpSearchBox = styled(TextField)(({ theme }) => ({
  width: '100%',
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(10px)',
    borderRadius: theme.spacing(2),
    fontFamily: "'EB Garamond', serif",
    fontSize: '1.1rem',
    boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
  },
}));

const FeedbackButton = styled(Button)(({ theme, active }) => ({
  position: 'absolute',
  bottom: theme.spacing(4),
  right: theme.spacing(4),
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1, 3),
  backgroundColor: active ? theme.palette.primary.dark : theme.palette.primary.main,
  color: '#fff',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  transition: 'all 0.3s ease',
  zIndex: 10,
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.dark : theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
  },
}));

export default function LeftCardView({ 
  cardData, 
  cardImages, 
  onCardClick, 
  index, 
  character, 
  onContinueAsk, 
  followUpCount,
  onViewChange,
  currentView,
  onJumpToSummary,
  onJumpToFollowUp,
}) {
  const cardRefs = useRef([]);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [followUpQuestion, setFollowUpQuestion] = useState('');
  const [showFeedback, setShowFeedback] = useState(false);

  useEffect(() => {
    cardRefs.current.forEach((card, idx) => {
      if (!card) return;

      const handleMouseMove = (e) => {
        const cardRect = card.getBoundingClientRect();
        const x = e.clientX - cardRect.left;
        const y = e.clientY - cardRect.top;
        const centerX = cardRect.width / 2;
        const centerY = cardRect.height / 2;
        
        const rotateX = ((y - centerY) / centerY) * 15;
        const rotateY = ((centerX - x) / centerX) * 15;
        
        const isHovered = index === idx;
        const scale = isHovered ? 1.05 : 1;
        card.style.transform = `scale(${scale}) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      };

      const handleMouseLeave = () => {
        const isHovered = index === idx;
        const scale = isHovered ? 1.05 : 1;
        card.style.transform = `scale(${scale}) rotateY(20deg)`;
      };

      if (index !== idx) {
        card.style.transform = 'scale(1) rotateY(20deg)';
      }

      card.addEventListener('mousemove', handleMouseMove);
      card.addEventListener('mouseleave', handleMouseLeave);

      return () => {
        card.removeEventListener('mousemove', handleMouseMove);
        card.removeEventListener('mouseleave', handleMouseLeave);
      };
    });
  }, [index]);

  const handleSummaryToggle = () => {
    const cardCount = cardData?.cards?.split(', ').length || 0;
    const isSummaryActive = index === cardCount;
    
    if (isSummaryActive) {
      // If already on summary, go back to first card
      onCardClick(0);
    } else {
      // If not on summary, jump directly to summary view
      if (onJumpToSummary) {
        onJumpToSummary();
      } else {
        // Fallback to old behavior
        onCardClick(cardCount);
      }
    }
  };

  const handleFollowUpToggle = () => {
    if (currentView === 'followup') {
      // If already on follow-up, go back to reading view
      onViewChange('reading');
    } else {
      // If not on follow-up, jump directly to follow-up view
      if (onJumpToFollowUp) {
        onJumpToFollowUp();
      } else {
        // Fallback to old behavior
        onViewChange('followup');
      }
    }
  };

  const handleSubmitFollowUp = () => {
    if (followUpQuestion.trim()) {
      onContinueAsk(followUpQuestion);
      setFollowUpQuestion('');
      setShowFollowUp(false);
    }
  };

  const handleFeedbackClick = () => {
    if (showFeedback) {
      setShowFeedback(false);
      const summaryIndex = cardData?.cards?.split(', ').length || 0; // 安全访问
      onCardClick(summaryIndex);
    } else {
      setShowFeedback(true);
      const feedbackIndex = (cardData?.cards?.split(', ').length || 0) + 1; // 安全访问
      onCardClick(feedbackIndex);
    }
  };

  const cards = cardData?.cards?.split(', ') || [];
  const cardCount = cards.length;
  const isSummaryActive = index === cardCount && currentView === 'reading';
  const isFollowUpActive = currentView === 'followup';

  const getCardStyle = (idx) => {
    const isSelected = index === idx;
    
    // If there's only one card (fortune reading), center it
    if (cardCount === 1) {
      return {
        left: '50%',
        transform: isSelected ? 'translateX(-50%) scale(1.05)' : 'translateX(-50%)',
        zIndex: 10,
        opacity: isSelected ? 1 : 0.9,
      };
    }
    
    // For multiple cards
    const baseLeft = idx * 170;
    
    const getZIndex = () => {
      if (isSelected) return 10;
      if (index === null) return 3 - idx;
      return 5 - Math.abs(index - idx);
    };

    // Add opacity and transform for selected state
    return {
      left: `${baseLeft}px`,
      zIndex: getZIndex(),
      opacity: isSelected ? 1 : 0.5,
      transform: isSelected ? 'scale(1.05)' : 'scale(1) rotateY(15deg)',
    };
  };

  return (
    <CardSection>
      <NavigationButtons>
        {/* Only show Summary button for multi-card readings */}
        {cardCount > 1 && (
          <NavButton
            variant="contained"
            active={isSummaryActive ? 1 : 0}
            onClick={handleSummaryToggle}
            startIcon={<LibraryBooksRoundedIcon />}
          >
            {"Summary"}
          </NavButton>
        )}
        
        {followUpCount > 0 && (
          <NavButton
            variant="contained"
            active={isFollowUpActive ? 1 : 0}
            onClick={handleFollowUpToggle}
            startIcon={<QuestionAnswerIcon />}
          >
            {`Follow-ups (${followUpCount})`}
          </NavButton>
        )}
      </NavigationButtons>

      <CardContainer>
        {cards.map((card, idx) => (
          <StyledCard 
            key={idx}
            ref={el => cardRefs.current[idx] = el}
            ishovered={String(index === idx)}
            isfortune={String(cardCount === 1)}
            isselected={String(index === idx)}
            sx={getCardStyle(idx)}
            onClick={() => onCardClick(idx)}
          >
            <CardImage 
              src={cardImages[card]} 
              alt={card} 
              draggable={false}
            />
            <CardTitle ishovered={String(index === idx)}>
              {card?.split(' ').slice(1).join(' ')}
            </CardTitle>
          </StyledCard>
        ))}
      </CardContainer>

      <Tooltip 
        title={showFeedback ? "Return to reading" : "Record how you feel"} 
        placement="left"
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 600 }}
      >
        {/* Remove the feedback button */}
      </Tooltip>
    </CardSection>
  );
}
